import { ReactElement, useMemo } from 'react'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { Instance, InstanceProps } from '@react-three/drei'
import { CONNECTOR_BOX_HEIGHT, CONNECTOR_BOX_OFFSET } from '../constants'
import {
  ConnectorKind,
  getConnectorBoxPositionWithOffset,
  getConnectorBoxRotation,
  getConnectorBoxScale,
  getConnectorHalfBoxHeight,
} from '../utils'

interface Props extends Omit<InstanceProps, 'ref' | 'position' | 'rotation' | 'scale'> {
  position: ImmutableVector3
  domain: Domain
  relativePosition: number
  connectorKind: ConnectorKind
  isTarget?: boolean
  height?: number
  offset?: number
}

const InstancedBoxInstance = ({
  position,
  domain,
  connectorKind,
  isTarget = false,
  height = CONNECTOR_BOX_HEIGHT,
  offset = CONNECTOR_BOX_OFFSET,
  ...props
}: Props): ReactElement => {
  const halfHeight = useMemo(() => getConnectorHalfBoxHeight(height), [height])

  const correctedPosition = useMemo(
    () => getConnectorBoxPositionWithOffset(position, isTarget, connectorKind, height, offset),
    [connectorKind, height, isTarget, offset, position],
  )

  const rotation = useMemo(() => getConnectorBoxRotation(domain), [domain])

  const scale = useMemo(
    () => getConnectorBoxScale(halfHeight, connectorKind),
    [halfHeight, connectorKind],
  )

  return (
    <Instance
      {...props}
      position={correctedPosition.v}
      rotation={rotation}
      scale={scale}
      onPointerOver={() => {
        document.body.style.cursor = 'pointer'
      }}
      onPointerOut={() => {
        document.body.style.cursor = 'auto'
      }}
    />
  )
}

export default InstancedBoxInstance
